'use client';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useAuth } from '~/auth/hooks';
import { Button } from '~/components';
import { MenuBack } from '~/menu/components';
import { useMenu } from '~/menu/hooks';
import { cn } from '~/style';

const links = [
  { title: 'Athletes', path: '/athletes' },
  { title: 'Workout', path: '/workout' },
];

export const Menu = () => {
  const { isVisible, toggle, hide } = useMenu();
  const pathname = usePathname();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hide();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggle, hide]);

  useEffect(() => {
    hide();
  }, [pathname, hide]);

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <MenuBack />

          <motion.div
            className="fixed bottom-0 left-0 right-0 top-20 z-40 flex flex-col sm:top-0 xl:flex-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="hide-scrollbar fixed bottom-0 left-0 right-0 top-0 overflow-auto max-md:pt-28 md:flex md:items-center md:justify-center">
              <div className="w-full space-y-8 pb-32 sm:space-y-10 md:pb-40">
                <Links />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export const Links = () => {
  const { isSignedIn } = useAuth();
  const { hide, isVisible } = useMenu();

  return (
    <div className="w-full">
      {links.map((link, linkIndex) => (
        <Link
          className={cn('mx-auto block max-w-xs md:max-w-xl', {
            'pointer-events-none': !isVisible,
          })}
          href={link.path}
          key={link.path}
          onClick={() => hide()}
        >
          <motion.div
            className="relative py-4 text-center text-xl font-extrabold uppercase sm:text-2xl md:py-6 md:text-3xl lg:py-10 lg:text-5xl"
            whileHover="hover"
            whileTap="tap"
            initial={{
              opacity: 0,
              y: -40,
            }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                delay: 0.2 + linkIndex * 0.1,
                type: 'spring',
              },
            }}
            exit={{
              opacity: 0,
              y: -40,
              transition: {
                delay: linkIndex * 0.1,
                type: 'spring',
              },
            }}
          >
            <motion.div
              initial={{ x: 0 }}
              transition={{
                type: 'spring',
                duration: 0.4,
              }}
              variants={{
                hover: {
                  scale: 1.04,
                },
                tap: {
                  scale: 0.98,
                },
              }}
            >
              {link.title}
            </motion.div>

            <div className="absolute right-0 top-1/2 hidden -translate-y-1/2">
              <motion.svg
                className="size-6 md:size-7 lg:size-9"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                initial={{
                  opacity: 0,
                  x: -20,
                }}
                variants={{
                  hover: {
                    opacity: 1,
                    x: 0,
                  },
                  tap: {
                    x: -5,
                  },
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                />
              </motion.svg>
            </div>
          </motion.div>
        </Link>
      ))}

      <motion.div
        className="pt-6 text-center md:pt-10"
        initial={{
          opacity: 0,
          y: -40,
        }}
        animate={{
          opacity: 1,
          y: 0,
          transition: {
            delay: 0.2 + links.length * 0.1,
            type: 'spring',
          },
        }}
        exit={{
          opacity: 0,
          y: -40,
          transition: {
            delay: links.length * 0.1,
            type: 'spring',
          },
        }}
      >
        <Link href={isSignedIn ? '/dashboard' : '/sign-up'}>
          <Button className="w-40 lg:w-60">
            {isSignedIn ? 'Dashboard' : 'Sign Up'}
          </Button>
        </Link>
      </motion.div>
    </div>
  );
};
